import React, { useState } from 'react';
import { useAppSelector } from '../stores/AppStore';
import { selectHasOneOfTheRoles } from '../features/user/userSlice';
import { useGetCustomerPortalUrlQuery } from '../services/apiSlice';
import { ROLES } from '../constants';

const ManageSubscription: React.FC = () => {
  const user = useAppSelector((state) => state.user.user);
  const isMentor = useAppSelector((state) =>
    selectHasOneOfTheRoles(state, [ROLES.mentor]),
  );
  const [isRedirecting, setIsRedirecting] = useState(false);

  const hasActiveSubscription = user?.billing?.hasActiveSubscription;
  const isPendingCancellation = user?.billing?.cancelAtPeriodEnd;
  const hasSubscriptionHistory = isMentor && user?.billing !== undefined;

  const {
    data: portalData,
    isLoading,
    error,
  } = useGetCustomerPortalUrlQuery(undefined, {
    skip: !hasSubscriptionHistory,
  });

  const handleRedirectToPortal = () => {
    if (portalData?.url) {
      setIsRedirecting(true);
      // Small delay to show the redirecting state
      setTimeout(() => {
        window.location.href = portalData.url;
      }, 500);
    }
  };

  if (!user || !hasSubscriptionHistory) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-gray-100 rounded-xl p-8 max-w-2xl mx-auto text-center">
          <h1 className="text-3xl font-medium mb-4">Access Denied</h1>
          <p className="text-gray-600">
            Only mentors can access subscription management.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-gray-100 rounded-xl p-8 max-w-2xl mx-auto text-center">
          <h1 className="text-3xl font-medium mb-4">
            Manage Your Subscription
          </h1>
          <div className="animate-pulse">
            <p className="text-gray-600">Loading subscription information...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-gray-100 rounded-xl p-8 max-w-2xl mx-auto text-center">
          <h1 className="text-3xl font-medium mb-4">
            Manage Your Subscription
          </h1>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">
              Unable to load subscription management. Please try again later or
              contact support.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-gray-100 rounded-xl p-8 max-w-2xl mx-auto">
        <h1 className="text-3xl font-medium mb-6 text-center">
          {hasActiveSubscription
            ? 'Manage Your Subscription'
            : 'Reactivate Your Subscription'}
        </h1>

        <div className="space-y-6">
          {/* Explanation section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <svg
                  className="w-6 h-6 text-blue-600 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-medium text-blue-900 mb-2">
                  {hasActiveSubscription
                    ? 'Secure Subscription Management'
                    : 'Reactivate Your Subscription'}
                </h3>
                <p className="text-blue-800 mb-3">
                  {hasActiveSubscription
                    ? "For your security and to comply with payment regulations, subscription management is handled through Stripe's secure Customer Portal."
                    : "Your subscription has expired. You can reactivate it through Stripe's secure Customer Portal."}
                </p>
                <p className="text-blue-700 text-sm">
                  You'll be redirected to a secure Stripe page where you can
                  {hasActiveSubscription
                    ? ' safely manage your subscription, update payment methods, view invoices, and make changes to your plan.'
                    : ' reactivate your subscription, update payment methods, and view your billing history.'}
                </p>
              </div>
            </div>
          </div>

          {/* Features list */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="font-medium text-gray-900 mb-4">
              {hasActiveSubscription
                ? 'In the Customer Portal, you can:'
                : 'To resubscribe, you can:'}
            </h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-center space-x-3">
                <svg
                  className="w-5 h-5 text-green-500 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Update your payment method</span>
              </li>
              <li className="flex items-center space-x-3">
                <svg
                  className="w-5 h-5 text-green-500 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>View and download invoices</span>
              </li>
              <li className="flex items-center space-x-3">
                <svg
                  className="w-5 h-5 text-green-500 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>
                  {hasActiveSubscription ? (
                    isPendingCancellation ? (
                      <>
                        <strong>Renew</strong> your subscription (by clicking on{' '}
                        <strong>Don't cancel subscription</strong>)
                      </>
                    ) : (
                      'Change or cancel your subscription'
                    )
                  ) : (
                    <strong>Reactivate your subscription</strong>
                  )}
                </span>
              </li>
              <li className="flex items-center space-x-3">
                <svg
                  className="w-5 h-5 text-green-500 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Update billing information</span>
              </li>
            </ul>
          </div>

          {/* Action button */}
          <div className="text-center space-y-4">
            <button
              onClick={handleRedirectToPortal}
              disabled={!portalData?.url || isRedirecting}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
            >
              {isRedirecting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>Redirecting...</span>
                </>
              ) : (
                <>
                  <span>Open Stripe Customer Portal</span>
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </>
              )}
            </button>

            <p className="text-sm text-gray-600">
              After managing your subscription, you can return to this app by
              clicking the "Return to The Think Tank Education Solutions, LLC"
              button in the Customer Portal, or by simply navigating back to{' '}
              <span className="font-mono text-blue-600">
                {window.location.origin}
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageSubscription;
